import React from 'react'
import i18next from 'i18next'
import moment from 'moment'
import {ColumnsType} from 'antd/es/table'
import {Currencies, DATE_FORMAT} from '@/constants'
import {Settlement} from '@/stores/settlements/models/Settlement'
import {
    SETTLEMENT_DATE_COLUMN_WIDTH,
    ADDRESS_COLUMN_WIDTH,
    POSTCODE_COLUMN_WIDTH,
    MID_COLUMN_WIDTH,
    TERMINAL_NUMBER_COLUMN_WIDTH,
    AMOUNT_COLUMN_WIDTH,
    SETTLEMENT_TYPE_COLUMN_WIDTH,
    TOTAL_FEE_COLUMN_WIDTH,
    INTERCHANGE_FEE_COLUMN_WIDTH,
    SCHEME_FEE_COLUMN_WIDTH,
    PROCESSING_FEE_COLUMN_WIDTH,
    GATEWAY_FEE_COLUMN_WIDTH,
    AUTHORISATION_FEE_COLUMN_WIDTH,
    BLENDED_FEE_COLUMN_WIDTH,
    REFUND_FEE_COLUMN_WIDTH,
    AMOUNT_TO_SETTLE_COLUMN_WIDTH,
    SORT_CODE_COLUMN_WIDTH,
    ACCOUNT_NUMBER_COLUMN_WIDTH,
    OPERATION_COLUMN_WIDTH,
    TRANSACTION_TYPE_COLUMN_WIDTH,
    TRANSACTION_ID_COLUMN_WIDTH,
    PAYOUT_REFERENCE_COLUMN_WIDTH,
    MERCHANT_REFERENCE_COLUMN_WIDTH,
    PAYMENT_METHOD_COLUMN_WIDTH,
    CARDHOLDER_NAME_COLUMN_WIDTH,
    CARD_TYPE_COLUMN_WIDTH,
    ISSUER_REGION_COLUMN_WIDTH,
    ISSUER_COUNTRY_COLUMN_WIDTH,
    IS_CORPORATE_CARD_COLUMN_WIDTH,
    CAPTURE_METHOD_COLUMN_WIDTH
} from '@/stores/settlements/constants'
import {getAmountWithCurrency} from '@/utils'
import {CardSchemeLabelWithIcon} from '@/components'

export const restOfColumns = (isMobile = false, settlements = []): ColumnsType<Settlement> => {
    return [
        {
            title: i18next.t('settlements.columns.settlementDate'),
            dataIndex: 'settlementDate',
            key: 'settlementDate',
            fixed: isMobile ? undefined : 'left',
            width: SETTLEMENT_DATE_COLUMN_WIDTH,
            render: (value: string) => Boolean(value) && moment(value).format(DATE_FORMAT)
        },
        {
            title: i18next.t('settlements.columns.address'),
            dataIndex: 'address',
            key: 'address',
            width: ADDRESS_COLUMN_WIDTH
        },
        {
            title: i18next.t('settlements.columns.postcode'),
            dataIndex: 'postcode',
            key: 'postcode',
            width: POSTCODE_COLUMN_WIDTH
        },
        {
            title: i18next.t('settlements.columns.mid'),
            dataIndex: 'mid',
            key: 'mid',
            width: MID_COLUMN_WIDTH
        },
        {
            title: i18next.t('settlements.columns.terminalNumber'),
            dataIndex: 'terminalNumber',
            key: 'terminalNumber',
            width: TERMINAL_NUMBER_COLUMN_WIDTH
        },
        {
            title: i18next.t('settlements.columns.amount'),
            dataIndex: 'amount',
            key: 'amount',
            width: AMOUNT_COLUMN_WIDTH,
            render: (item, object) => (
                <b>{getAmountWithCurrency(item, object.currency as Currencies)}</b>
            )
        },
        {
            title: i18next.t('settlements.columns.settlementType'),
            dataIndex: 'settlementType',
            key: 'amount',
            width: SETTLEMENT_TYPE_COLUMN_WIDTH
        },
        {
            title: i18next.t('settlements.columns.totalFee'),
            dataIndex: 'totalFee',
            key: 'totalFee',
            width: TOTAL_FEE_COLUMN_WIDTH,
            render: (item, object) => (
                <b>{getAmountWithCurrency(item, object.currency as Currencies)}</b>
            )
        },
        {
            title: i18next.t('settlements.columns.interchangeFee'),
            dataIndex: 'interchangeFee',
            key: 'interchangeFee',
            width: INTERCHANGE_FEE_COLUMN_WIDTH,
            render: (item, object) => (
                <b>{getAmountWithCurrency(item, object.currency as Currencies)}</b>
            )
        },
        {
            title: i18next.t('settlements.columns.schemeFee'),
            dataIndex: 'schemeFee',
            key: 'schemeFee',
            width: SCHEME_FEE_COLUMN_WIDTH,
            render: (item, object) => (
                <b>{getAmountWithCurrency(item, object.currency as Currencies)}</b>
            )
        },
        {
            title: i18next.t('settlements.columns.processingFee'),
            dataIndex: 'processingFee',
            key: 'processingFee',
            width: PROCESSING_FEE_COLUMN_WIDTH,
            render: (item, object) => (
                <b>{getAmountWithCurrency(item, object.currency as Currencies)}</b>
            )
        },
        {
            title: i18next.t('settlements.columns.gatewayFee'),
            dataIndex: 'gatewayFee',
            key: 'gatewayFee',
            width: GATEWAY_FEE_COLUMN_WIDTH,
            render: (item, object) => (
                <b>{getAmountWithCurrency(item, object.currency as Currencies)}</b>
            )
        },
        {
            title: i18next.t('settlements.columns.authorisationFee'),
            dataIndex: 'authorisationFee',
            key: 'authorisationFee',
            width: AUTHORISATION_FEE_COLUMN_WIDTH,
            render: (item, object) => (
                <b>{getAmountWithCurrency(item, object.currency as Currencies)}</b>
            )
        },
        {
            title: i18next.t('settlements.columns.blendedFee'),
            dataIndex: 'blendedFee',
            key: 'blendedFee',
            width: BLENDED_FEE_COLUMN_WIDTH,
            render: (item, object) => (
                <b>{getAmountWithCurrency(item, object.currency as Currencies)}</b>
            )
        },
        {
            title: i18next.t('settlements.columns.refundFee'),
            dataIndex: 'refundFee',
            key: 'refundFee',
            width: REFUND_FEE_COLUMN_WIDTH,
            render: (item, object) => (
                <b>{getAmountWithCurrency(item, object.currency as Currencies)}</b>
            )
        },
        {
            title: i18next.t('settlements.columns.dccRebate'),
            dataIndex: 'dccRebateAmount',
            key: 'dccRebateAmount',
            width: 120,
            render: (item, object) => <b>{getAmountWithCurrency(item)}</b>
        },
        {
            title: i18next.t('settlements.columns.amountToSettle'),
            dataIndex: 'amountToSettle',
            key: 'amountToSettle',
            width: AMOUNT_TO_SETTLE_COLUMN_WIDTH,
            render: (item, object) => (
                <b>{getAmountWithCurrency(item, object.currency as Currencies)}</b>
            )
        },
        {
            title: i18next.t('settlements.columns.sortCode'),
            dataIndex: 'sortCode',
            key: 'sortCode',
            width: SORT_CODE_COLUMN_WIDTH
        },
        {
            title: i18next.t('settlements.columns.accountNumber'),
            dataIndex: 'accountNumber',
            key: 'accountNumber',
            width: ACCOUNT_NUMBER_COLUMN_WIDTH
        },
        {
            title: i18next.t('settlements.columns.operation'),
            dataIndex: 'operation',
            key: 'operation',
            width: OPERATION_COLUMN_WIDTH
        },
        {
            title: i18next.t('settlements.columns.transactionType'),
            dataIndex: 'transactionType',
            key: 'transactionType',
            width: TRANSACTION_TYPE_COLUMN_WIDTH,
            render: (item) =>
                item
                    ? i18next.t(`enums:transactionTypes.${item.toUpperCase()}`, {
                          defaultValue: item.toUpperCase()
                      })
                    : ''
        },
        {
            title: i18next.t('settlements.columns.transactionId'),
            dataIndex: 'transactionId',
            key: 'transactionId',
            width: TRANSACTION_ID_COLUMN_WIDTH
        },
        {
            title: i18next.t('settlements.columns.merchantReference'),
            dataIndex: 'merchantReference',
            key: 'merchantReference',
            width: MERCHANT_REFERENCE_COLUMN_WIDTH
        },
        {
            title: i18next.t('settlements.columns.paymentMethod'),
            dataIndex: 'cardMask',
            key: 'cardMask',
            width: PAYMENT_METHOD_COLUMN_WIDTH,
            render: (item, settlement) => {
                if (!item) {
                    return ''
                }

                return <CardSchemeLabelWithIcon cardScheme={settlement.cardScheme} title={item} />
            }
        },
        {
            title: i18next.t('settlements.columns.cardholderName'),
            dataIndex: 'cardholderName',
            key: 'cardholderName',
            width: CARDHOLDER_NAME_COLUMN_WIDTH
        },
        {
            title: i18next.t('settlements.columns.cardType'),
            dataIndex: 'cardType',
            key: 'cardType',
            width: CARD_TYPE_COLUMN_WIDTH
        },
        {
            title: i18next.t('settlements.columns.issuerRegion'),
            dataIndex: 'issuerRegion',
            key: 'issuerRegion',
            width: ISSUER_REGION_COLUMN_WIDTH
        },
        {
            title: i18next.t('settlements.columns.issuerCountry'),
            dataIndex: 'issuerCountry',
            key: 'issuerCountry',
            width: ISSUER_COUNTRY_COLUMN_WIDTH
        },
        {
            title: i18next.t('settlements.columns.isCorporateCard'),
            dataIndex: 'isCorporateCard',
            key: 'isCorporateCard',
            width: IS_CORPORATE_CARD_COLUMN_WIDTH,
            render: (value) =>
                value === undefined || value === null
                    ? undefined
                    : value === true
                      ? i18next.t('settlements.columnValues.corporate')
                      : i18next.t('settlements.columnValues.nonCorporate')
        },
        {
            title: i18next.t('settlements.columns.captureMethod'),
            dataIndex: 'captureMethod',
            key: 'captureMethod',
            width: CAPTURE_METHOD_COLUMN_WIDTH,
            render: (item) =>
                item ? i18next.t(`enums:captureMethods.${item}`, {defaultValue: item}) : ''
        }
    ]
}
